/**
 * Sanitizes pasted content for GSM SMS encoding compatibility.
 * Replaces curly quotes and apostrophe (from Word/Google Docs/web) with straight
 * equivalents so message stays in GSM (160 chars) instead of switching to UCS2 (70 chars).
 * Only runs on paste; direct keyboard input of these characters is left unchanged.
 */

import { $insertDataTransferForRichText } from "@lexical/clipboard";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getSelection, COMMAND_PRIORITY_HIGH, PASTE_COMMAND } from "lexical";
import { useEffect } from "react";

const STRAIGHT_DOUBLE = '"';
const STRAIGHT_SINGLE = "'";
const STRAIGHT_DASH = "-";
const STRAIGHT_ELLIPSIS = "...";

/** Replaces literal curly chars and variant punctuation with straight equivalents. Used for plain text and HTML. */
function sanitizeForGsm(text: string): string {
  return (
    text
      .replaceAll("\u201C", STRAIGHT_DOUBLE) // " left double quote
      .replaceAll("\u201D", STRAIGHT_DOUBLE) // " right double quote
      .replaceAll("\u2018", STRAIGHT_SINGLE) // ' left single quote
      .replaceAll("\u2019", STRAIGHT_SINGLE) // ' right single quote / apostrophe
      // Dashes / hyphens / minus variants
      .replaceAll("\u002D", STRAIGHT_DASH) // -
      .replaceAll("\u2010", STRAIGHT_DASH) // ‐
      .replaceAll("\u2011", STRAIGHT_DASH) // non-breaking hyphen
      .replaceAll("\u2012", STRAIGHT_DASH) // ‒
      .replaceAll("\u2013", STRAIGHT_DASH) // –
      .replaceAll("\u2014", STRAIGHT_DASH) // —
      .replaceAll("\u2015", STRAIGHT_DASH) // ―
      .replaceAll("\u2043", STRAIGHT_DASH) // ⁃
      .replaceAll("\u2212", STRAIGHT_DASH) // −
      .replaceAll("\uFE58", STRAIGHT_DASH) // ﹘
      .replaceAll("\uFE63", STRAIGHT_DASH) // ﹣
      .replaceAll("\uFF0D", STRAIGHT_DASH) // －
      // Other punctuation
      .replaceAll("\u2026", STRAIGHT_ELLIPSIS)
  ); // …
}

/** HTML often contains entities for curly quotes and punctuation; replace those too so parsed content is GSM-safe. */
function sanitizeHtmlForGsm(html: string): string {
  let s = sanitizeForGsm(html);
  s = s
    .replaceAll("&ldquo;", STRAIGHT_DOUBLE)
    .replaceAll("&rdquo;", STRAIGHT_DOUBLE)
    .replaceAll("&lsquo;", STRAIGHT_SINGLE)
    .replaceAll("&rsquo;", STRAIGHT_SINGLE)
    .replaceAll("&#8220;", STRAIGHT_DOUBLE)
    .replaceAll("&#8221;", STRAIGHT_DOUBLE)
    .replaceAll("&#8216;", STRAIGHT_SINGLE)
    .replaceAll("&#8217;", STRAIGHT_SINGLE)
    // Dash / hyphen / minus entities
    .replaceAll("&ndash;", STRAIGHT_DASH)
    .replaceAll("&mdash;", STRAIGHT_DASH)
    .replaceAll("&horbar;", STRAIGHT_DASH)
    .replaceAll("&minus;", STRAIGHT_DASH)
    .replaceAll("&#45;", STRAIGHT_DASH)
    .replaceAll("&#8208;", STRAIGHT_DASH)
    .replaceAll("&#8209;", STRAIGHT_DASH)
    .replaceAll("&#8210;", STRAIGHT_DASH)
    .replaceAll("&#8211;", STRAIGHT_DASH)
    .replaceAll("&#8212;", STRAIGHT_DASH)
    .replaceAll("&#8213;", STRAIGHT_DASH)
    .replaceAll("&#8259;", STRAIGHT_DASH)
    .replaceAll("&#8722;", STRAIGHT_DASH)
    .replaceAll("&#65112;", STRAIGHT_DASH)
    .replaceAll("&#65123;", STRAIGHT_DASH)
    .replaceAll("&#65293;", STRAIGHT_DASH)
    // Ellipsis
    .replaceAll("&hellip;", STRAIGHT_ELLIPSIS)
    .replaceAll("&#8230;", STRAIGHT_ELLIPSIS);
  // Hex entities (case-insensitive for x and digits)
  s = s
    .replace(/&#x201C;/gi, STRAIGHT_DOUBLE)
    .replace(/&#x201D;/gi, STRAIGHT_DOUBLE)
    .replace(/&#x2018;/gi, STRAIGHT_SINGLE)
    .replace(/&#x2019;/gi, STRAIGHT_SINGLE)
    .replace(/&#x2D;/gi, STRAIGHT_DASH)
    .replace(/&#x2010;/gi, STRAIGHT_DASH)
    .replace(/&#x2011;/gi, STRAIGHT_DASH)
    .replace(/&#x2012;/gi, STRAIGHT_DASH)
    .replace(/&#x2013;/gi, STRAIGHT_DASH)
    .replace(/&#x2014;/gi, STRAIGHT_DASH)
    .replace(/&#x2015;/gi, STRAIGHT_DASH)
    .replace(/&#x2043;/gi, STRAIGHT_DASH)
    .replace(/&#x2212;/gi, STRAIGHT_DASH)
    .replace(/&#xFE58;/gi, STRAIGHT_DASH)
    .replace(/&#xFE63;/gi, STRAIGHT_DASH)
    .replace(/&#xFF0D;/gi, STRAIGHT_DASH)
    .replace(/&#x2026;/gi, STRAIGHT_ELLIPSIS);
  return s;
}

export default function PasteSanitizePlugin(): null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      (event) => {
        if (!event || !(event instanceof ClipboardEvent)) return false;
        const clipboardData = event.clipboardData;
        if (!clipboardData) return false;

        const plain = clipboardData.getData("text/plain");
        const html = clipboardData.getData("text/html");
        if (!plain && !html) return false;

        const sanitizedPlain = sanitizeForGsm(plain);
        const sanitizedHtml = sanitizeHtmlForGsm(html);
        if (plain === sanitizedPlain && html === sanitizedHtml) return false;

        event.preventDefault();

        const sanitizedDataTransfer = new DataTransfer();
        sanitizedDataTransfer.setData("text/plain", sanitizedPlain);
        sanitizedDataTransfer.setData("text/html", sanitizedHtml);

        editor.update(
          () => {
            const selection = $getSelection();
            if (selection !== null) {
              $insertDataTransferForRichText(
                sanitizedDataTransfer,
                selection,
                editor
              );
            }
          },
          { tag: "paste-sanitize" }
        );

        return true;
      },
      COMMAND_PRIORITY_HIGH
    );
  }, [editor]);

  return null;
}
